// Class recipes for the flow landing's stage bodies (polish.P6, #623). They
// restate the finished Cases table idiom (`Scene.tsx` `CasesFlowPanels`) so
// every stage body under the strip reads as one product: a flush
// `SurfacePagePanel`, a surface-2 table with a sticky header, hairline rows,
// the row title over a tracked-mono meta line, and US$ right-aligned
// (DESIGN.md § The Advisory standard).

/** Internal scroller under a flush panel header; the header row sticks. */
export const STAGE_TABLE_SCROLL = 'max-h-[20rem] overflow-auto';

/** `Table` face and `THead` fill: one surface-2 plane, header pinned on scroll. */
export const STAGE_TABLE = 'bg-[var(--surface-2)]';
export const STAGE_THEAD = 'sticky top-0 z-10 bg-[var(--surface-2)]';

/** Header cells: the first column aligns with the panel title's inset. */
export const STAGE_TH_FIRST = 'whitespace-nowrap px-4 py-2 text-xs';
export const STAGE_TH = 'whitespace-nowrap px-2 py-2 text-xs';

/** Body cells, same insets as the header. */
export const STAGE_TD_FIRST = 'px-4 py-2';
export const STAGE_TD = 'px-2 py-2';

/** Row title and its meta line (code · age), as in the Cases table. */
export const STAGE_ROW_TITLE = 'text-sm font-medium leading-snug text-ink-primary';
export const STAGE_ROW_META = 'mt-0.5 font-mono text-[10px] text-muted';

/** Right-aligned money cell: the figure over its comparison. */
export const STAGE_MONEY = 'whitespace-nowrap text-sm font-semibold tabular-nums text-ink-primary';
export const STAGE_MONEY_COMPARISON = 'mt-0.5 text-[10px] leading-snug text-muted';
/** An unpriced row names what it feeds instead of a figure. */
export const STAGE_FEEDS = 'text-xs leading-snug text-muted';

/** A selected or tracked row takes the selection wash (8% crimson), as the tracked case does. */
export const STAGE_ROW_SELECTED =
  'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] hover:bg-[color-mix(in_srgb,var(--color-accent-500)_12%,var(--surface-2))] dark:hover:bg-[color-mix(in_srgb,var(--color-accent-500)_12%,var(--surface-2))]';

/** Hairline-divided rows inside a card (never nested bordered boxes). */
export const STAGE_ROWS = 'divide-y divide-divider';

/** A clickable list row: the surface step on hover, no lift. */
export const STAGE_ROW_BUTTON =
  'w-full text-left transition-colors duration-150 hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)]';

/** Caps label for fact labels and in-card subheads (`label-caps`). */
export const STAGE_LABEL_CAPS = 'text-xs font-semibold uppercase tracking-[0.07em] text-muted';

/** In-card link that stays a link (a trace or a download), in ink, not crimson. */
export const STAGE_INK_LINK =
  'text-xs font-medium text-ink-secondary underline decoration-[var(--hairline-strong)] underline-offset-2 hover:text-ink-primary';

/** The one nested box a card may hold: a decision gate (comp `.gate`). */
export const STAGE_GATE_BOX =
  'rounded-[14px] border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-3.5 py-3';

/**
 * A `Pill` in the state tones (comp `.p-bad`): a breach-grade chip reads
 * `--state-blocked` on its 9% wash, never crimson and never the generic red.
 * Pass as the `Pill` className; other tones keep the Pill's own face.
 */
export function statePillClass(tone: string | undefined): string | undefined {
  return tone === 'danger'
    ? 'bg-[color-mix(in_srgb,var(--state-blocked)_9%,transparent)] text-[var(--state-blocked)] dark:bg-[color-mix(in_srgb,var(--state-blocked)_13%,transparent)] dark:text-[var(--state-blocked)]'
    : undefined;
}
